import * as moment from 'moment';
import {IParsedKN15Telegram} from './kn15-telegram/kn15-parse.service';
import {IStation, IDischargeModel} from '../../api/api.types';
import {StateService, StateParams} from 'angular-ui-router';
import {
    IDailyDataRequest,
    IDataValueApi,
    IDailyDataParams,
    IMeteoDataParams
} from '../../api/data-value-api.service';
import {Stack} from 'immutable';
import {IImomoErrorService} from '../../error/error.service';
import {IParsedTelegramData} from './telegram.controller';
import hydrologicalYear from '../../utils/hydrological-year';
import {DailyDataService} from './daily-data.service';
import {IDischargeModelApi} from '../../api/discharge-model-api.service';
import {
  TelegramDataComparisonService,
  ITelegramComparisonData
} from './telegram-data-comparison.service';
import {IFinalTelegramData} from './data-processing.controller'

/** 
 * State parameters handled by the controller.
 */
interface IOperationalInputStateParams extends StateParams {
  telegrams?:string[];
}

/**
 * Steps required for submitting operational data.
 */
enum OperationalInputSteps {
  Telegram, DataOverview, DataProcessing, Save
}

export namespace IInputEvents {
  export const INPUT_SUBMITTED = 'imomo-input-submitted';
}

export interface IProcessedDataCell {
  waterLevel: number;
  discharge: number;
  highlighted: boolean;
}

/**
 * Controller for the operational input tool.
 */
export default class OperationalInputController {
  static $inject = ['$stateParams', '$state', 'dataValueApi', 'imomoErrorService', '$q', 
    'dailyDataService', 'dischargeModelApi', 'telegramDataComparisonService', '$rootScope', '$translate'];
  /**
   * Index of the current step/selected tab.
   */
  selectedTabIndex:OperationalInputSteps;
  /**
   * Whether the telegram step was completed successfully.
   */
  telegramStepValid:boolean;
  /**
   * Whether the discharge curve step was completed successfully.
   */
  dischargeModelStepValid:boolean;
  /**
   * Input telegram.
   */
  telegrams:string[];

  completedTelegrams:IParsedTelegramData[];

  telegramsRequiringAction: IParsedTelegramData[];

  telegramDataForProcessing: ITelegramComparisonData[];

  automaticallyProcessedTelegramDailyDataParamsCollection: IFinalTelegramData[] = [];

  /**
   * Station associated with the telegram.
   */
  station:IStation;
  /**
   * Discharge model to use for data processing.
   */
  dischargeModel:IDischargeModel;
  /**
   * Whether the discharge model was created in this session.
   */
  createdDischargeModel:boolean = false;
  /**
   * Processed data in the last step.
   */
  processedData:IFinalTelegramData[];
  /**
   * Whether data submission is in progress.
   */
  submitInProgress:boolean;

  parsedTelegramDataCollection:IParsedTelegramData[];
  unprocessedTelegrams: string[]

  constructor(private $stateParams:IOperationalInputStateParams,
              private $state:StateService,
              private dataValueApi:IDataValueApi,
              private imomoErrorService: IImomoErrorService,
              private $q: ng.IQService,
              private dailyDataService: DailyDataService,
              private dischargeModelApi: IDischargeModelApi,
              private telegramDataComparisonService: TelegramDataComparisonService,
              private $rootScope: ng.IRootScopeService,
              private $translate:ng.translate.ITranslateService) {
    this.telegrams = $stateParams.telegrams || [];
    this.$rootScope.$on('MODEL_CHANGE', () => {
      this.onTelegramProcessed(this.parsedTelegramDataCollection, this.unprocessedTelegrams);
    });

  }

  /**
   * Hook to process changes to the telegram parameter.
   */
  uiOnParamsChanged(newParams:IOperationalInputStateParams) {
    this.telegrams = newParams.telegrams || [];
    this.selectedTabIndex = 0;
  }

  /**
   * Whether the button for the next step can be clicked.
   */
  get nextButtonDisabled():boolean {
    switch (this.selectedTabIndex) {
      case OperationalInputSteps.Telegram:
        return !this.telegramStepValid;
      case OperationalInputSteps.DataOverview:
        return false;
      case OperationalInputSteps.DataProcessing:
        return false;
      default:
        return true;
    }
  }

  /**
   * Whether the button for previous button can be clicked.
   */
  get previousButtonDisabled():boolean {
    return this.selectedTabIndex == OperationalInputSteps.Telegram;
  }

  /**
   * Whether the button for submitting the data can be shown.
   */
  get submitButtonShown():boolean {
    return this.selectedTabIndex == OperationalInputSteps.Save;
  }

  /**
   * Whether the button for submitting data can be clicked.
   */
  get submitButtonDisabled():boolean {
    return this.processedData && !this.processedData.length;
  }

  /**
   * Advances one step/tab.
   */
  goToNextStep() {
    this.selectedTabIndex++;
  }

  /**
   * Goes back one step/tab.
   */
  goToPreviousStep() {
    this.selectedTabIndex--;
  }

  /**
   * Stores the processed telegram and loaded station data.
   */
  onTelegramProcessed(parsedTelegramDataCollection:IParsedTelegramData[], unprocessedTelegrams: string[]) {
    this.parsedTelegramDataCollection = parsedTelegramDataCollection;
    this.unprocessedTelegrams = unprocessedTelegrams;
    this.splitTelegramData(parsedTelegramDataCollection)
      .then((splittedTelegramData) => {
        this.completedTelegrams = splittedTelegramData.completedTelegrams;
        this.telegramsRequiringAction = splittedTelegramData.telegramsRequiringAction;
        this.telegramDataForProcessing = splittedTelegramData.telegramDataForProcessing;

        this.telegramStepValid = 
          this.completedTelegrams && this.completedTelegrams.length != 0 || 
          this.telegramsRequiringAction && this.telegramsRequiringAction.length != 0;

        this.dischargeModelStepValid = this.telegramStepValid
            && this.dischargeModelStepValid;

        if (unprocessedTelegrams && unprocessedTelegrams.length) {
          this.$state.go('.',
              {telegrams: unprocessedTelegrams});
        }

      })

  }

  private splitTelegramData(parsedTelegramDataCollection: IParsedTelegramData[]) {
    // this function has to calculate the data that would be submitted to the daily_data_new endpoint
    const deferred = this.$q.defer<any>();
    let telegramDataPromises: ng.IPromise<any>[] = [];
    let dischargeModelAndParsedTelegramPromises: ng.IPromise<any>[] = [];
    let dailyDataAndDischargeModelAndParsedTelegramPromises: ng.IPromise<any>[] = [];
    let telegramsRequiringAction: IParsedTelegramData[] = [];
    let telegramDataForProcessing:  ITelegramComparisonData[] = [];
    let parsedTelegramDataCollectionCached = angular.copy(parsedTelegramDataCollection);
    let automaticallyProcessedTelegrams: IParsedTelegramData[] = parsedTelegramDataCollectionCached;
    this.automaticallyProcessedTelegramDailyDataParamsCollection = [];
    
    // get discharge models for telegram data
    angular.forEach(parsedTelegramDataCollection, (parsedTelegramData) => {
      if (parsedTelegramData.waterLevelData || parsedTelegramData.dischargeData) {
        const dischargeModelPromise = this.dischargeModelApi
                                        .getDischargeModels(parsedTelegramData.station.id, moment(parsedTelegramData.overriddenDate).year()); // hydrologicalYear(parsedTelegramData.overriddenDate)
        // parsedTelegramData is passed down through the promises for the convenience sake                                          
        const dischargeModelAndParsedTelegramPromise = this.$q.all([dischargeModelPromise, parsedTelegramData]);
        dischargeModelAndParsedTelegramPromises.push(dischargeModelAndParsedTelegramPromise);

      // meteo data only station
      } else {
        const dischargeModelAndParsedTelegramPromise = this.$q.all([null, parsedTelegramData]);
        dischargeModelAndParsedTelegramPromises.push(dischargeModelAndParsedTelegramPromise);
      }
    });

    if (dischargeModelAndParsedTelegramPromises && dischargeModelAndParsedTelegramPromises.length) { 
      // get daily data using discharge models
      this.$q.all(dischargeModelAndParsedTelegramPromises)
        .then((dischargeModelsAndParsedTelegrams) => { 
          let dataCheckPromises: ng.IPromise<any>[] = [];
          let chronologicalDischargeModel: IDischargeModel;
          let previousDischargeModel: IDischargeModel;

          dischargeModelsAndParsedTelegrams.forEach(([dischargeModels, parsedTelegramData]) => {
            if (dischargeModels && dischargeModels.length) {
              dischargeModels.forEach((dischargeModel: IDischargeModel, i: number) => {
                if (
                  !chronologicalDischargeModel &&
                  (!dischargeModel.validFrom ||
                  moment.unix(dischargeModel.validFrom) <= moment.unix(parsedTelegramData.overriddenDate))
                ) {
                  chronologicalDischargeModel = dischargeModel;
                  if ( dischargeModels.length > 1) {
                    previousDischargeModel = dischargeModels[i + 1];
                  }
                }
              });

              const deferred = this.$q.defer<any>();
              // check daily data 
              this.dailyDataService.getDailyDataAtOnce(parsedTelegramData, chronologicalDischargeModel, this.createdDischargeModel, previousDischargeModel)
                .then((dailyData) => {
                    let calculatedTelegramData: IDailyDataParams = {
                      dischargeData: {
                        date: parsedTelegramData.overriddenDate,
                        siteId: parsedTelegramData.station.id,
                        dailyData: dailyData,
                        dischargeModelParams: {
                          dischargeModel: chronologicalDischargeModel,
                          createdDischargeModel: this.createdDischargeModel
                        },
                        dischargeMeasurementData: parsedTelegramData.dischargeData,
                        temperatures: parsedTelegramData.temperatureData,
                        icePhenomenae: parsedTelegramData.icePhenomenaCardData.icePhenomenae
                      }
                    };

                    if (parsedTelegramData.meteoData && parsedTelegramData.meteoData.siteId) {
                      calculatedTelegramData.meteoData = parsedTelegramData.meteoData;
                    }

                    this.dataValueApi.checkDailyData(calculatedTelegramData)
                      .then((checkedData) => {
                        deferred.resolve([checkedData, calculatedTelegramData, parsedTelegramData]);
                      })
                      .catch((error) => {
                        this.imomoErrorService.showErrorDetailsDialog(error.data.details);
                      })

                })

              dataCheckPromises.push(deferred.promise);
            } else {
              if (parsedTelegramData.waterLevelData || parsedTelegramData.dischargeData) {
                this.$translate.instant('NO_DISCHARGE_CURVE_FOR_SITE_WITH_CODE', {siteCode: parsedTelegramData.station.siteCode})
              } else if (parsedTelegramData.meteoData && parsedTelegramData.meteoData.siteId) {
                const deferred = this.$q.defer<any>();

                let calculatedTelegramData: IDailyDataParams = {
                  meteoData: parsedTelegramData.meteoData
                };

                this.dataValueApi.checkDailyData(calculatedTelegramData).then((checkedData) => {
                  deferred.resolve([checkedData, calculatedTelegramData, parsedTelegramData]);
                })
                dataCheckPromises.push(deferred.promise);
              }

            }
          }); 

          this.$q.all(dataCheckPromises)
          .then((values) => {
            
            values.forEach(([checkedData, calculatedTelegramData, parsedTelegramData]) => {
              const comparisonData = this.telegramDataComparisonService.formatDataForTelegramProcessing(calculatedTelegramData, checkedData);
              let isHqAdjustmentEnabled: boolean = parsedTelegramData.isHqAdjustmentEnabled;
              let isEntryInDatabase: boolean = 
                comparisonData.dailyData.length || 
                comparisonData.dischargeDataRows.length || 
                comparisonData.meteoDataRows.length
                ? 
                true : false;
              let isWaterLevelTrendDifferent: boolean;
              let isIcePhenomenaIncluded: boolean;
              if (checkedData.dischargeData) {
                isWaterLevelTrendDifferent = this.checkWaterLevelTrend(parsedTelegramData, checkedData.dischargeData.dailyData); 
                console.log('isWaterLevelTrendDifferent', isWaterLevelTrendDifferent)
                isIcePhenomenaIncluded = 
                  calculatedTelegramData.dischargeData.icePhenomenae && calculatedTelegramData.dischargeData.icePhenomenae.length ? true : false;
              }

              // Telegrams requiring action
              if (
                isHqAdjustmentEnabled || 
                isEntryInDatabase || 
                isWaterLevelTrendDifferent || 
                isIcePhenomenaIncluded
              ) { 
                telegramsRequiringAction.push(parsedTelegramData);
                telegramDataForProcessing.push({
                  comparisonData: this.telegramDataComparisonService.formatDataForTelegramProcessing(calculatedTelegramData, checkedData),
                  actions: {
                    hqAdjustment: isHqAdjustmentEnabled,
                    entryCorrection: isEntryInDatabase,
                    waterLevelTrend: isWaterLevelTrendDifferent,
                    icePhenomena: isIcePhenomenaIncluded
                  },
                  calculatedTelegramData: calculatedTelegramData,
                  parsedTelegramData
                });
                automaticallyProcessedTelegrams.splice(_.findLastIndex(automaticallyProcessedTelegrams, {
                  telegramText: parsedTelegramData.telegramText
                }), 1)
              } else {
                this.automaticallyProcessedTelegramDailyDataParamsCollection.push({
                  dailyDataParams: calculatedTelegramData,
                  parsedTelegramData: parsedTelegramData
                });
              }
            })
            
            deferred.resolve({
              completedTelegrams: automaticallyProcessedTelegrams,
              telegramsRequiringAction: telegramsRequiringAction,
              telegramDataForProcessing: telegramDataForProcessing
            });
          })
          .catch((error) => {
            this.imomoErrorService.showErrorDetailsDialog(error.data.details);
          })
        })
        .catch((error) => {
          deferred.reject(error)
        });
    }

    return deferred.promise;
  }

  /**
   * Stores the processed daily data.
   */
  onDataProcessed(telegramsRequiringActionFinalData:IFinalTelegramData[], createdDischargeModel: boolean) {
    this.processedData = 
      telegramsRequiringActionFinalData
        .concat(this.automaticallyProcessedTelegramDailyDataParamsCollection); 
    this.createdDischargeModel = createdDischargeModel
  }

  private checkWaterLevelTrend(parsedTelegramData: IParsedTelegramData, oldEntryDailyData: IDailyDataRequest[]) {
    let waterLevelTrendInDatabase: number; 

    angular.forEach(oldEntryDailyData, (dailyTriplet, index) => {
      if (dailyTriplet.morningData && dailyTriplet.morningData.waterLevel && oldEntryDailyData[index - 1] &&
          oldEntryDailyData[index - 1].morningData) {
        waterLevelTrendInDatabase = dailyTriplet.morningData.waterLevel -
            oldEntryDailyData[index - 1].morningData.waterLevel;
      }
    }); 

    return typeof waterLevelTrendInDatabase === 'undefined' ? 
      false : parsedTelegramData.waterLevelData.waterLevelTrend !== waterLevelTrendInDatabase;
  }

  /**
   * Submits the processed data and created discharge model to the backend.
   */
  submitData() {
    this.submitInProgress = true;

    let dailyDataPromises: ng.IPromise<any>[] = [];
    this.processedData.forEach((telegramDailyDataParams: IFinalTelegramData) => {
      this.dataValueApi.storeDailyData(telegramDailyDataParams.dailyDataParams)
        .catch((error) => { 
          this.imomoErrorService.showErrorDetailsDialog(telegramDailyDataParams.parsedTelegramData.station.siteCode + ': ' + error.data.details);
        });
    })
    
    this.$q.all(dailyDataPromises)
      .then(() => {
        this.$state.go('.', {telegrams: ''});
      }).catch((error)=> {
        this.imomoErrorService.showErrorDetailsDialog(error.data.details);
      }).finally(() => {
        this.submitInProgress = false;
        this.resetView();
        this.$rootScope.$broadcast(IInputEvents.INPUT_SUBMITTED);
      });
  }

  private resetView() {
    this.telegramStepValid = false;
    this.$stateParams.telegrams = null;
    this.telegrams = [];
    this.processedData = [];
    this.completedTelegrams = [];
    this.telegramsRequiringAction = [];
    this.telegramDataForProcessing = [];
    this.automaticallyProcessedTelegramDailyDataParamsCollection = [];
  }

}



/** WEBPACK FOOTER **
 ** ./app/operational/input/input.controller.ts
 **/